import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, scopes, prompt, b2cPolicies } from 'client/utils/auth/authConfig';
import { logWithPrefix } from 'client/utils/logging';
import { MsalProvider } from '@azure/msal-react';
import { ProfileService } from 'client/services';
import { USER_PROFILE_NOT_FOUND_PAGE } from '../routes/routeConstants';
import { redirect } from 'react-router';
import { portalBaseUrl } from 'client/utils/constants/frontEndConfig';
const log = logWithPrefix('MSAL');

// line deepcode ignore NoHardcodedPasswords: Not a password, just the code B2C sends back to indicate a password reset flow
const PASSWORD_RESET_CODE = 'AADB2C90118';
const NO_ACCOUNT_CODE = 'AADB2C90075';

// Create a single MSAL instance to reference throughout the application.
const msalInstance = new PublicClientApplication(msalConfig);

const initializeMsal = async () => {
    // We have to initialise MSAL before we can call any other MSAL APIs
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#initializing-the-publicclientapplication-object
    return msalInstance.initialize().then(() => {
        /**
         * Set an active account on MSAL after a successful login.
         * @param {Object} response - response from a successful sign in request.
         * @returns {null}
         */
        msalInstance.handleRedirectPromise().then(async response => {
            if (response) {
                const accounts = msalInstance.getAllAccounts();
                msalInstance.setActiveAccount(accounts[0]);
                await ProfileService.patchLastLogindate();
            }
        });

        // Handling other event callbacks
        msalInstance.addEventCallback(async (event) => {
            if (!event) {
                return;
            }

            // Log certain events. Can only log when authed, so can't log all events.
            // possible events: https://azuread.github.io/microsoft-authentication-library-for-js/ref/enums/_azure_msal_browser.eventtype.html
            const loggableEvents = [EventType.LOGIN_SUCCESS, EventType.LOGOUT_FAILURE];
            const localAccountId = event.payload?.account?.localAccountId;
            if (loggableEvents.includes(event.eventType)) {
                log(`Event ${event.eventType}`, localAccountId);
            }

            // Handle various events
            if (event.eventType === EventType.LOGIN_FAILURE) {
                const errorMessage = event?.error?.errorMessage || '';
                if (errorMessage.includes(PASSWORD_RESET_CODE)) {
                    const { authority } = b2cPolicies.authorities.forgotPassword;
                    await msalInstance.loginRedirect({ scopes, prompt, extraQueryParameters: { ts: Date.now() }, authority });
                } else if (errorMessage.includes(NO_ACCOUNT_CODE)) {
                    // We got an error back from b2c, so redirect to profile not found page
                    // If we try to redirect using react-router here it doesn't work as it shows us the error message
                    // instead of redirecting, so we use window.location.href instead.
                    window.location.href = USER_PROFILE_NOT_FOUND_PAGE;
                } else {
                    redirect(portalBaseUrl);
                }
            }
        });
    });
};

if (process.env.NODE_ENV === 'development') {
    // In dev mode, awaiting it interferes with hot reloading, so do it but ignore the promise
    // eslint-disable-next-line no-void
    void initializeMsal();
} else {
    await initializeMsal();
}

// Wrapper for the login redirect
const loginRedirect = async (params) => {
    await msalInstance.handleRedirectPromise();
    return msalInstance.loginRedirect({ scopes, prompt, extraQueryParameters: { ts: Date.now() }, ...params });
};

// Wrapper for the logout redirect
const logoutRedirect = async (postLogoutRedirectUri = msalConfig.auth.postLogoutRedirectUri) => {
    await log('Logging User Out', postLogoutRedirectUri).promise;
    const account = msalInstance.getActiveAccount();
    return msalInstance.logoutRedirect({ account, postLogoutRedirectUri });
};

const getAccountOrGoToLoginPage = async () => {
    // handleRedirectPromise returns an accessToken but it is stale, so don't cache it;
    // Otherwise, if you leave app open for ~13 minutes then make a request
    // using that accessToken, you get token expired failures.
    await msalInstance.handleRedirectPromise();
    const account = msalInstance.getActiveAccount();
    if (!account) {
        await loginRedirect();
        return '';
    }
    return account;
};

// allows us to control what the home page loader does
const hasActiveAccount = () => {
    return Boolean(msalInstance.getActiveAccount());
};

/**
 * Gets an access token from the MSAL instance if an active account is set.
 * If a post-login redirect has just occurred, return the access token from
 * that response. Otherwise check for an active account and acquire a token.
 * If there's no active account, abort getting a token and redirect to the login
 * flow.
 * @resolves {String} The access token.
 */
const getAccessToken = async (newOrganisationId = null) => {
    const account = await getAccountOrGoToLoginPage();
    const organisationId = newOrganisationId || account.idTokenClaims.organisationId;
    // Getting accesstoken out of acquireTokenSilent ensures that it is bang up to date
    const response = await msalInstance.acquireTokenSilent({
        scopes,
        account,
        tokenQueryParameters: { organisationId },
        forceRefresh: Boolean(newOrganisationId)
    });
    return response.accessToken;
};

const setOrganisationId = async (organisationId = '') => {
    log('Setting Organisation ID', organisationId);
    return getAccessToken(organisationId);
};

const MsalProviderWrapper = ({ children }) => {
    // This avoids having to export the msalInstance from this file - everything that needs it
    // should use `useMsal` and not directly import the instance.
    return <MsalProvider instance={msalInstance}>
        {children}
    </MsalProvider>;
};

export {
    MsalProviderWrapper,
    getAccountOrGoToLoginPage,
    hasActiveAccount,
    setOrganisationId,
    getAccessToken,
    loginRedirect,
    logoutRedirect
};
